import type { RatingTrendPoint } from "../../components/ui/rating-sparkline";
import { rpc } from "../../rpc";
import type {
  FormAnswer,
  Priority,
  QuestionType,
  ReactionSummary,
  ValueRecognition,
} from "../../types";

export type { Priority };

// ============================================================================
// Shared Types for Review / Feed Pages
// ============================================================================

export type ReviewQueueItem = {
  submissionId: string;
  formId: string;
  memberName: string;
  memberProfilePictureUrl: string | null;
  formTitle: string;
};

export type FormInfo = {
  id: string;
  title: string;
  orgId: string;
  orgName: string;
  orgLogoUrl: string | null;
  periodStart: Date | null;
  periodEnd: Date | null;
  prioritiesEnabled: boolean;
  valuesRecognitionEnabled: boolean;
};

export type QuestionInfo = {
  id: string;
  title: string;
  description: string;
  type: QuestionType;
  ratingMax: number;
  seq: number;
  required: boolean;
};

export type SubmissionInfo = {
  id: string;
  periodStart: Date;
  submittedAt: Date | null;
  memberName: string | null;
  memberProfilePictureUrl: string | null;
  memberId: string | null;
  answers: FormAnswer[];
  reviewedAt: Date | null;
  reviewedBy: string | null;
  canReview: boolean;
  /** Manager's review state (shown to admins viewing someone else's report) */
  managerReviewedAt?: Date | null;
  managerReviewedBy?: string | null;
};

export type PrioritiesState = {
  currentPeriod: Priority[];
  previousPeriod: Priority[];
  currentPeriodStart: Date | null;
  previousPeriodStart: Date | null;
};

export type FullSubmissionData = {
  form: FormInfo;
  questions: QuestionInfo[];
  submission: SubmissionInfo;
  currentMemberId: string;
  priorities: PrioritiesState | null;
  answerCommentCounts: Record<string, number>;
  priorityCommentCounts: Record<string, number>;
  answerReactions: Record<string, ReactionSummary[]>;
  priorityReactions: Record<string, ReactionSummary[]>;
  recognitions: ValueRecognition[];
  recognitionCommentCounts: Record<string, number>;
  recognitionReactions: Record<string, ReactionSummary[]>;
  /** Rating trends keyed by question ID */
  ratingTrends: Record<string, RatingTrendPoint[]>;
};

// ============================================================================
// Full Submission Loader (shared between review and feed pages)
// ============================================================================

export async function loadFullSubmission(
  orgId: string,
  item: ReviewQueueItem,
): Promise<FullSubmissionData> {
  const result = await rpc.submissions.getFormForFilling({
    formId: item.formId,
    submissionId: item.submissionId,
  });

  if (!result.submission) {
    throw new Error(`Submission ${item.submissionId} not found`);
  }
  if (!result.currentMemberId) {
    throw new Error("Not a workspace member");
  }

  const submission = result.submission;
  const form = result.form;

  // Phase 1: priorities + answer batch data
  let priorities: PrioritiesState | null = null;
  let answerCommentCounts: Record<string, number> = {};
  let answerReactions: Record<string, ReactionSummary[]> = {};

  const periodStart = submission.periodStart ?? form.periodStart;
  const answerIds = (submission.answers ?? [])
    .filter((a) => a.id)
    .map((a) => a.id as string);

  const phase1: Promise<void>[] = [];

  if (form.prioritiesEnabled && periodStart && submission.memberId) {
    const currentPeriodStart = new Date(periodStart);
    // Compute previous period start from actual period length (not hardcoded 7 days)
    const periodEnd = form.periodEnd ? new Date(form.periodEnd) : null;
    const periodLengthMs = periodEnd
      ? periodEnd.getTime() - currentPeriodStart.getTime()
      : 7 * 24 * 60 * 60 * 1000; // fallback to 7 days
    const prevDate = new Date(currentPeriodStart.getTime() - periodLengthMs);

    phase1.push(
      (async () => {
        const [currentResult, previousResult] = await Promise.all([
          rpc.priorities
            .getMemberPriorities({
              workspaceId: orgId,
              memberId: submission.memberId!,
              periodStart: currentPeriodStart,
            })
            .catch(() => ({ priorities: [], memberName: "" })),
          rpc.priorities
            .getMemberPriorities({
              workspaceId: orgId,
              memberId: submission.memberId!,
              periodStart: prevDate,
            })
            .catch(() => ({ priorities: [], memberName: "" })),
        ]);
        priorities = {
          currentPeriod: currentResult.priorities,
          previousPeriod: previousResult.priorities,
          currentPeriodStart,
          previousPeriodStart: prevDate,
        };
      })(),
    );
  }

  // Rating trends for sparklines
  let ratingTrends: Record<string, RatingTrendPoint[]> = {};
  const ratingQuestions = result.questions.filter((q) => q.type === "rating");
  if (ratingQuestions.length > 0 && submission.memberId) {
    phase1.push(
      rpc.submissions
        .getRatingTrends({
          workspaceId: orgId,
          formId: item.formId,
          memberIds: [submission.memberId],
          periodCount: 8,
          upToPeriod: submission.periodStart
            ? new Date(submission.periodStart).toISOString()
            : undefined,
        })
        .then((r) => {
          if (r.trends[submission.memberId!]) {
            ratingTrends = r.trends[submission.memberId!] ?? {};
          }
        })
        .catch(() => {
          /* non-critical */
        }),
    );
  }

  if (answerIds.length > 0 && result.currentMemberId) {
    phase1.push(
      rpc.comments
        .getCommentCounts({
          workspaceId: orgId,
          targetType: "form_answer",
          targetIds: answerIds,
        })
        .then((r) => {
          answerCommentCounts = r.counts;
        })
        .catch(() => {
          /* non-critical batch data */
        }),
    );
    phase1.push(
      rpc.reactions
        .getReactionSummaries({
          workspaceId: orgId,
          targetType: "form_answer",
          targetIds: answerIds,
        })
        .then((r) => {
          answerReactions = r.summaries;
        })
        .catch(() => {
          /* non-critical batch data */
        }),
    );
  }

  await Promise.all(phase1);

  // Phase 2: priority batch data
  let priorityCommentCounts: Record<string, number> = {};
  let priorityReactions: Record<string, ReactionSummary[]> = {};

  if (priorities && result.currentMemberId) {
    const allPriorityIds = [
      ...(priorities as PrioritiesState).previousPeriod.map((p) => p.id),
      ...(priorities as PrioritiesState).currentPeriod.map((p) => p.id),
    ];
    if (allPriorityIds.length > 0) {
      const [pcCounts, pcReactions] = await Promise.all([
        rpc.comments
          .getCommentCounts({
            workspaceId: orgId,
            targetType: "priority",
            targetIds: allPriorityIds,
          })
          .catch(() => ({ counts: {} as Record<string, number> })),
        rpc.reactions
          .getReactionSummaries({
            workspaceId: orgId,
            targetType: "priority",
            targetIds: allPriorityIds,
          })
          .catch(() => ({
            summaries: {} as Record<string, ReactionSummary[]>,
          })),
      ]);
      priorityCommentCounts = pcCounts.counts;
      priorityReactions = pcReactions.summaries;
    }
  }

  // Phase 3: Load recognitions if enabled
  let recognitions: ValueRecognition[] = [];
  let recognitionCommentCounts: Record<string, number> = {};
  let recognitionReactions: Record<string, ReactionSummary[]> = {};

  if (form.valuesRecognitionEnabled && submission.memberId && periodStart) {
    try {
      const recResult = await rpc.valueRecognitions.listByAuthor({
        workspaceId: orgId,
        authorMemberId: submission.memberId,
        periodStart: new Date(periodStart),
      });
      recognitions = recResult.recognitions;

      const recIds = recognitions.map((r) => r.id);
      if (recIds.length > 0 && result.currentMemberId) {
        const [rcCounts, rcReactions] = await Promise.all([
          rpc.comments
            .getCommentCounts({
              workspaceId: orgId,
              targetType: "value_recognition",
              targetIds: recIds,
            })
            .catch(() => ({ counts: {} as Record<string, number> })),
          rpc.reactions
            .getReactionSummaries({
              workspaceId: orgId,
              targetType: "value_recognition",
              targetIds: recIds,
            })
            .catch(() => ({
              summaries: {} as Record<string, ReactionSummary[]>,
            })),
        ]);
        recognitionCommentCounts = rcCounts.counts;
        recognitionReactions = rcReactions.summaries;
      }
    } catch {
      // Silently fail - recognitions are optional
    }
  }

  return {
    form,
    questions: result.questions,
    submission,
    currentMemberId: result.currentMemberId,
    priorities,
    answerCommentCounts,
    priorityCommentCounts,
    answerReactions,
    priorityReactions,
    recognitions,
    recognitionCommentCounts,
    recognitionReactions,
    ratingTrends,
  };
}
